import {
    AsAllowed,
    AsHidden,
    AsRestricted,
    Restricted,
    LimitationProps
} from '~/components/Builder/Common/CreationLimitations/creationLimitations.types';
import i18next from 'i18next';
import { last } from 'lodash';
import invariant from 'ts-invariant';
import { i18n } from '~/i18n/config';

export const forbidContractBlockInPublicFlow = ({
    targetFlow
}: LimitationProps<'targetFlow'>) => {
    const t = i18next.getFixedT(null, 'builder', 'creation_limitation_dialog');

    if (targetFlow.isLeadForm) {
        return AsHidden(
            t('lead_form_not_allowed_reason', {
                action_block: 'contract'
            })
        );
    }

    return AsAllowed;
};

export interface ForbidContractWithRecapsBeforeServiceSelectionRestriction
    extends Restricted {
    issueType: 'forbidContractWithRecapsBeforeServiceSelection';
    solutionData: {
        droppedItemId: string;
        dropTargetId: string;
        direction: 'top' | 'bottom';
        newPageOrder: number;
        blockType: 'contract_block';
        compositionType: undefined;
        newPageName: string;
    };
}

export const forbidContractWithRecapsBeforeServiceSelection = ({
    targetFlow,
    targetPage,
    sourceBlock
}: LimitationProps<'targetFlow' | 'targetPage' | 'sourceBlock'>) => {
    const lastServiceSelectionPage = last(
        targetFlow.pages.filter(
            currPage => currPage.actionBlock?.isServiceSelectionBlock
        )
    );

    invariant(!!sourceBlock, 'a block must be provided');
    invariant(
        sourceBlock.type === 'contract_block',
        'expected the provided block to be contract block'
    );

    // if there's no service recaps on contract, or no service selection blocks, then creation is allowed.
    if (!sourceBlock.hasServicesRecaps || !lastServiceSelectionPage) {
        return AsAllowed;
    }

    const pageOrder = targetPage?.order ?? Infinity; // if no page is provided - that means it's a new page.

    if (pageOrder < lastServiceSelectionPage.order) {
        const t = i18n.getFixedT(null, 'builder');
        return AsRestricted<ForbidContractWithRecapsBeforeServiceSelectionRestriction>(
            {
                issueType: 'forbidContractWithRecapsBeforeServiceSelection',
                solutionData: {
                    direction: 'bottom',
                    droppedItemId: targetPage.page_id,
                    dropTargetId: lastServiceSelectionPage.page_id,
                    newPageOrder: lastServiceSelectionPage.order,
                    blockType: 'contract_block',
                    compositionType: undefined,
                    newPageName: t('default_page_titles.contract')
                },
                reason: {
                    short: 'Add contract after services?',
                    long: 'Your client needs to select services so they can be added to the contract services recap.',
                    cta: 'ADD AFTER SERVICES',
                    rejectCta: 'DO NOT ADD'
                }
            }
        );
    }

    return AsAllowed;
};
